import {
    MouseEvent,
    Dispatch,
    ReactElement,
    FC,
    SetStateAction,
    useEffect,
    useRef,
    useState,
    SyntheticEvent,
    MutableRefObject,
} from 'react';
import { usePrevious } from '@shared/hooks/usePrevious';
import { Box, Popover } from '@mui/material';
import Chip from '@mui/material/Chip';
import { ChipProps } from '@mui/material/Chip/Chip';
import OverflowTooltip from '@shared/components/ui/OverflowTooltip';

const MAP_LINE_COUNT_TO_HEIGHT = {
    1: 20,
    2: 52,
    3: 80,
};

interface OverflowChipListProps {
    labelAtStart?: ReactElement | string;
    array: any[];
    getLabel: (item: any) => string;
    getId: (item: any) => string | number;
    lineCount?: 1 | 2 | 3;
    chipProps?: any;
}

const OverflowChipList: FC<OverflowChipListProps> = ({
    labelAtStart,
    array,
    getLabel,
    getId,
    lineCount = 1,
    chipProps = {},
}) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [hiddenChipsIds, setHiddenChipsIds] = useState<(string | number)[]>([]);
    const { onDelete, sx, ...restChipProps } = chipProps;
    const prevArrayLength = usePrevious(array.length);
    const ref = useRef<HTMLElement>();

    useEffect(() => {
        // if items were deleted from original array, delete it from hidden ids array

        if (Array.isArray(array) && prevArrayLength !== array.length) {
            setHiddenChipsIds((ids) => {
                return ids.filter((id) => array.map((item) => item.pk).includes(id));
            });
        }
    }, [array, prevArrayLength]);

    const handleOpenHiddenChipsPopover = (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleCloseHiddenChipsPopover = () => {
        setAnchorEl(null);
    };

    const preventPropagation = (e: SyntheticEvent) => {
        e.stopPropagation();
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: lineCount === 1 ? 'initial' : 'flex-end',
                gap: 1,
                maxWidth: '100%',
            }}
            ref={ref}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: lineCount === 1 ? 'nowrap' : 'wrap',
                    gap: 1,
                    rowGap: 0.5,
                    minWidth: 0,
                    maxWidth: 'calc(100% - 40px)',
                    maxHeight: lineCount === 1 ? 'initial' : `${MAP_LINE_COUNT_TO_HEIGHT[lineCount]}px`,
                    overflow: 'hidden',
                }}
            >
                {labelAtStart && (
                    <Box sx={{ fontWeight: 700, display: 'flex', alignItems: 'center' }}>{labelAtStart}</Box>
                )}
                {array.map((item) => {
                    const id = getId(item);
                    const label = getLabel(item);

                    return (
                        <OverflowChip
                            key={id}
                            chipId={id}
                            setHiddenChipsIds={setHiddenChipsIds}
                            containerRef={ref}
                            sx={{
                                fontSize: '0.8125rem',
                                ...sx,
                            }}
                            label={
                                <OverflowTooltip
                                    title={label}
                                    sx={{
                                        display: 'block',
                                        fontSize: '0.875rem',
                                        lineHeight: '143%',
                                        letterSpacing: '0.01063rem',
                                    }}
                                >
                                    {label}
                                </OverflowTooltip>
                            }
                            size="small"
                            onDelete={onDelete ? () => onDelete(id) : undefined}
                            {...restChipProps}
                        />
                    );
                })}
            </Box>
            {Boolean(hiddenChipsIds.length) && (
                <Chip
                    sx={{
                        fontSize: '0.8125rem',
                        flexShrink: 0,
                        marginLeft: 'auto',
                    }}
                    size="small"
                    variant="outlined"
                    label={`+${hiddenChipsIds.length}`}
                    onClick={handleOpenHiddenChipsPopover}
                    onMouseDown={preventPropagation}
                />
            )}
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleCloseHiddenChipsPopover}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                onMouseDown={preventPropagation}
            >
                <Box
                    sx={{
                        maxWidth: '300px',
                        maxHeight: '300px',
                        overflow: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: 1,
                        p: 1,
                    }}
                >
                    {array
                        .filter((item) => hiddenChipsIds.includes(getId(item)))
                        .map((item) => {
                            const id = getId(item);
                            const label = getLabel(item);

                            return (
                                <Chip
                                    key={id}
                                    sx={sx}
                                    label={
                                        <OverflowTooltip
                                            title={label}
                                            sx={{
                                                fontSize: '0.875rem',
                                                lineHeight: '143%',
                                                letterSpacing: '0.01063rem',
                                                display: 'block',
                                            }}
                                        >
                                            {label}
                                        </OverflowTooltip>
                                    }
                                    size="small"
                                />
                            );
                        })}
                </Box>
            </Popover>
        </Box>
    );
};

export default OverflowChipList;

interface OverflowChipProps extends ChipProps {
    chipId: number;
    setHiddenChipsIds: Dispatch<SetStateAction<number[]>>;
    containerRef: MutableRefObject<HTMLElement>;
}

const OverflowChip: FC<OverflowChipProps> = ({ chipId, setHiddenChipsIds, containerRef, ...props }) => {
    const ref = useRef(null);
    const [isVisible, setIsVisible] = useState(true);

    useEffect(() => {
        isVisible
            ? setHiddenChipsIds((prev: number[]) => prev.filter((id) => id !== chipId))
            : setHiddenChipsIds((prev: number[]) => Array.from(new Set([...prev, chipId])));
    }, [chipId, isVisible, setHiddenChipsIds]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsVisible(entry.isIntersecting && entry.intersectionRatio > 0.9); // if 90% of element is visible, consider it visible in container
            },
            {
                root: containerRef.current, // observe intersection only in Chip's container
            },
        );

        ref.current && observer.observe(ref.current);

        return () => {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            ref.current && observer.unobserve(ref.current);
        };
    }, [containerRef]);

    return (
        <Chip
            ref={ref}
            size="small"
            style={{
                pointerEvents: isVisible ? undefined : 'none',
                opacity: isVisible ? 1 : 0,
                transition: 'opacity 0.2s ease-in',
            }}
            {...props}
        />
    );
};
